import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { templatesApply } from '@/shared/api/generated/content/content';
import type { TemplateBody, TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { toast } from '@/shared/ui/Toast';

import styles from './TemplatesDialog.module.scss';
import { useTemplates } from './useTemplates';

export interface TemplatesDialogProps {
  siteID: string;
  env: EnvKey;
  /** Раздел: шаблоны моделей или форм. */
  purpose: TemplateBody['purpose'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Модель из шаблона (шаг 7.6): готовый набор — коллекция с полями и
 * правилами доступа. После создания это обычная модель, её правят как
 * угодно; заведённая открывается сразу.
 */
export function TemplatesDialog({
  siteID,
  env,
  purpose,
  open,
  onOpenChange,
}: TemplatesDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string>();

  const list = useTemplates(siteID, env, purpose);

  const apply = (key: string) => {
    setBusy(key);

    templatesApply(siteID, kinds[env], key)
      .then((response) => {
        const type = response.data as TypeBody;

        toast.success(`«${type.name}» заведена`);
        void queryClient.invalidateQueries({ queryKey: ['content-types', siteID] });
        void queryClient.invalidateQueries({ queryKey: ['site-users', siteID] });
        onOpenChange(false);
        void navigate({
          to:
            type.purpose === 'form'
              ? '/sites/$siteId/$env/forms/$typeId'
              : '/sites/$siteId/$env/models/$typeId',
          params: { siteId: siteID, env, typeId: type.id },
        });
      })
      .catch((cause: unknown) => {
        toast.error('Не удалось завести', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setBusy(undefined);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <DialogContent
          title={purpose === 'form' ? 'Форма из шаблона' : 'Модель из шаблона'}
          description={
            purpose === 'form'
              ? 'Готовые поля и правила: форма сразу принимает заявки с сайта. Дальше меняйте как угодно.'
              : 'Готовый набор полей и правил доступа. После создания это обычная модель — меняйте как угодно.'
          }
          size="md"
        >
          {list.isPending ? (
            <p className={styles['muted']}>Загружаем шаблоны…</p>
          ) : (
            <ul className={styles['list']}>
              {(list.data ?? []).map((template) => (
                <li key={template.key} className={styles['item']}>
                  <div>
                    <p className={styles['name']}>{template.name}</p>
                    <p className={styles['muted']}>{template.description}</p>
                    {(template.models?.length ?? 0) > 1 || template.users ? (
                      <p className={styles['muted']}>
                        Заведёт: {(template.models ?? []).join(', ')}
                        {template.users ? ' · включит вход посетителей («Пользователи»)' : ''}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    loading={busy === template.key}
                    disabled={busy !== undefined}
                    onClick={() => {
                      apply(template.key);
                    }}
                  >
                    Завести
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
